// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Typography, Box, Divider, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useAuthContext } from "@asgardeo/auth-react";
import { CustomerHealthDetailTable } from '../../components/CustomerHealthDetail';
import { RESOURCE_SERVER_URLS } from '../../data/configs/spl-config';

interface FilterPayload {
    email: string;
    phrase: string;
    risks: string;
    region: string;
    healthStatus: string;
}

interface AccountNavState {
    accountList: { accountSysId: string; accountName: string }[];
    currentIndex: number;   
    absoluteIndex: number; 
    totalCount: number;
    filterPayload: FilterPayload;
}

const CustomerHealthDetail: React.FC = () => {

    const { accountId } = useParams<{ accountId: string }>();
    const [accountName, setAccountName] = useState<string>("");
    const [fetchingNav, setFetchingNav] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { httpRequest, getAccessToken } = useAuthContext();

    const navState = location.state as AccountNavState | null;
    const accountList = navState?.accountList ?? [];
    const currentIndex = navState?.currentIndex ?? -1;
    const absoluteIndex = navState?.absoluteIndex ?? -1;
    const totalCount = navState?.totalCount ?? 0;

    const hasPrev = absoluteIndex > 0;
    const hasNext = absoluteIndex >= 0 && absoluteIndex < totalCount - 1;

    const fetchAccountAtIndex = async (targetIdx: number): Promise<{ accountSysId: string; accountName: string } | null> => {
        try {
            const token = await getAccessToken();
            const response = await httpRequest({
                url: RESOURCE_SERVER_URLS[0] + '/customer-health/summary',
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'x-jwt-assertion': token },
                data: { ...navState!.filterPayload, offset: targetIdx, limit: 1 },
            });
            const result = response.data as { data: { accountSysId: string; accountName: string }[]; totalCount: number };
            return result.data[0] ?? null;
        } catch {
            return null;
        }
    };

    const navigateTo = async (targetAbsoluteIdx: number) => {
        if (targetAbsoluteIdx < 0 || targetAbsoluteIdx >= totalCount) return;

        // Check if the target falls within the currently loaded page
        const localRangeStart = absoluteIndex - currentIndex;
        const localRangeEnd = localRangeStart + accountList.length - 1;

        if (targetAbsoluteIdx >= localRangeStart && targetAbsoluteIdx <= localRangeEnd) {
            const localIdx = targetAbsoluteIdx - localRangeStart;
            navigate(`/support/customer-health/account/${accountList[localIdx].accountSysId}`, {
                state: { ...navState, currentIndex: localIdx, absoluteIndex: targetAbsoluteIdx }
            });
            return;
        }

        // Target is on a different page  fetch just that one account
        setFetchingNav(true);
        const account = await fetchAccountAtIndex(targetAbsoluteIdx);
        setFetchingNav(false);
        if (account) {
            navigate(`/support/customer-health/account/${account.accountSysId}`, {
                state: {
                    accountList: [account],
                    currentIndex: 0,
                    absoluteIndex: targetAbsoluteIdx,
                    totalCount,
                    filterPayload: navState!.filterPayload,
                }
            });
        }
    };

    if (!accountId) {
         return (
             <Box sx={{ p: 3 }}>
                 <Link to="/support/customer-health" style={{ textDecoration: 'none' }}>
                     <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', color: '#666', '&:hover': { color: '#e96900' } }}>
                         <ArrowBackIcon sx={{ fontSize: '1rem' }} />Back to Dashboard
                     </Box>
                 </Link>
             </Box>
         );
    }

    const navButtonSx = {
        fontSize: '0.8rem',
        textTransform: 'none',
        borderColor: '#ddd',
        color: '#555',
        '&:hover:not(:disabled)': { borderColor: '#e96900', color: '#e96900' },
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Link to="/support/customer-health" style={{ textDecoration: 'none' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', color: '#666', '&:hover': { color: '#e96900' } }}>
                        <ArrowBackIcon sx={{ fontSize: '1rem' }} />Back to Dashboard
                    </Box>
                </Link>

                {absoluteIndex >= 0 && totalCount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!hasPrev || fetchingNav}
                            onClick={() => navigateTo(absoluteIndex - 1)}
                            startIcon={fetchingNav
                                ? <CircularProgress size={12} sx={{ color: 'inherit' }} />
                                : <ArrowBackIosNewIcon sx={{ fontSize: '0.75rem !important' }} />
                            }
                            sx={navButtonSx}
                        >
                            Previous
                        </Button>
                        <Typography variant="caption" sx={{ color: '#888', minWidth: 64, textAlign: 'center' }}>
                            {absoluteIndex + 1} / {totalCount}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!hasNext || fetchingNav}
                            onClick={() => navigateTo(absoluteIndex + 1)}
                            endIcon={fetchingNav
                                ? <CircularProgress size={12} sx={{ color: 'inherit' }} />
                                : <ArrowForwardIosIcon sx={{ fontSize: '0.75rem !important' }} />
                            }
                            sx={navButtonSx}
                        >
                            Next
                        </Button>
                    </Box>
                )}
            </Box>

            <Box sx={{ mt: 1, mb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.5rem', color: '#212121', lineHeight: 1.3 }}>
                        {accountName || 'Customer Health Analysis'}
                    </Typography>
                    {accountName && (
                        <Typography variant="body2" sx={{ color: '#888', fontWeight: 400, fontSize: '0.9rem' }}>
                            Health Analysis
                        </Typography>
                    )}
                </Box>
                <Divider sx={{ mt: 1.5 }} />
            </Box>

            <CustomerHealthDetailTable
                accountId={accountId}
                onAccountNameLoaded={setAccountName}
            />

        </Box>
    );
};

export default CustomerHealthDetail;
